/**
 * Schedule Control Component
 * 
 * Allows users to set start and end dates for notification bar display
 * 
 * @package TopBuddy
 */

import { Dropdown, Button, DateTimePicker, ButtonGroup, SelectControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import ProBadge from '../ProBadge';

const ScheduleControl = ({ 
	scheduleType = 'date_range',
	onScheduleTypeChange,
	startDate, 
	endDate, 
	onStartDateChange, 
	onEndDateChange, 
	selectedDays = [], 
	onSelectedDaysChange 
}) => {
	const postSettingsData = window.topbuddyPostSettings || {};
	const isProActive = postSettingsData.isProActive || false;
	const upgradeUrl = postSettingsData.upgradeUrl || 'https://wptopbuddy.com/?utm_source=plugin&utm_medium=topbuddy-free-plugin&utm_campaign=topbuddy-free-plugin-admin';
	const timezoneOffset = parseInt(postSettingsData.timezoneOffset || 0, 10);

	const days = [
		{ key: 'mon', label: __('Mon', 'topbuddy') },
		{ key: 'tue', label: __('Tue', 'topbuddy') },
		{ key: 'wed', label: __('Wed', 'topbuddy') },
		{ key: 'thu', label: __('Thu', 'topbuddy') },
		{ key: 'fri', label: __('Fri', 'topbuddy') },
		{ key: 'sat', label: __('Sat', 'topbuddy') },
		{ key: 'sun', label: __('Sun', 'topbuddy') },
	];

	const toggleDay = (dayKey) => {
		const newSelectedDays = selectedDays.includes(dayKey)
			? selectedDays.filter((d) => d !== dayKey)
			: [...selectedDays, dayKey];
		onSelectedDaysChange(newSelectedDays);
	};

	// Helper to convert Site Time String (from DB) to Local Date Object (for Picker)
	const toLocal = (siteDateString) => {
		if (!siteDateString) return null;
		// Ensure ISO format
		const isoString = siteDateString.replace(' ', 'T');
		// Parse as UTC to get the raw values
		const d = new Date(isoString + 'Z');
		// Adjust for site offset (subtract offset to get UTC timestamp of the represented time)
		const utcTimestamp = d.getTime() - (timezoneOffset * 1000);
		return new Date(utcTimestamp);
	};

	// Helper to convert Local Date String (from Picker) to Site Time String (for DB)
	const toSite = (localDateString) => {
		if (!localDateString) return null;
		const localDate = new Date(localDateString);
		// Get UTC timestamp
		const utcTimestamp = localDate.getTime();
		// Add offset to get Site Time represented as UTC
		const siteTimestamp = utcTimestamp + (timezoneOffset * 1000);
		const siteDate = new Date(siteTimestamp);
		// Format as YYYY-MM-DD HH:mm:ss
		return siteDate.toISOString().slice(0, 19).replace('T', ' ');
	};

	const handleStartDateChange = (date) => {
		onStartDateChange(toSite(date));
	};

	const handleEndDateChange = (date) => {
		onEndDateChange(toSite(date));
	};

	const handleClearStartDate = () => {
		onStartDateChange(null);
	};

	const handleClearEndDate = () => {
		onEndDateChange(null);
	};

	const localStartDate = toLocal(startDate);
	const localEndDate = toLocal(endDate);

	return (
		<div className="topbuddy-schedule-control">
			<div style={{ opacity: isProActive ? 1 : 0.5, pointerEvents: isProActive ? 'auto' : 'none' }}>
				<div className="topbuddy-schedule-control__field">
					<SelectControl
						label={__('Schedule Type', 'topbuddy')}
						value={scheduleType}
						options={[
							{ label: __('Always Show', 'topbuddy'), value: 'always' },
							{ label: __('Date Range', 'topbuddy'), value: 'date_range' },
							{ label: __('Days of the Week', 'topbuddy'), value: 'days_of_week' },
						]}
						onChange={onScheduleTypeChange}
					/>
				</div>

				{scheduleType === 'date_range' && (
					<>
						<div className="topbuddy-schedule-control__field">
							<label className="topbuddy-settings-field__label">
								{__('Start Date & Time', 'topbuddy')}
							</label>
							<div className="topbuddy-schedule-control__input-group">
								<Dropdown
									className="topbuddy-datetime-dropdown"
									contentClassName="topbuddy-datetime-dropdown__content"
									popoverProps={{ placement: 'bottom-start' }}
									renderToggle={({ isOpen, onToggle }) => (
										<Button
											onClick={onToggle}
											aria-expanded={isOpen}
											variant="secondary"
										>
											{localStartDate ? localStartDate.toLocaleString() : __('Select start date & time', 'topbuddy')}
										</Button>
									)}
									renderContent={() => (
										<DateTimePicker
											currentDate={localStartDate}
											onChange={handleStartDateChange}
											is12Hour={false}
										/>
									)}
								/>
								{startDate && (
									<Button
										variant="tertiary"
										isDestructive
										onClick={handleClearStartDate}
										className="topbuddy-schedule-control__clear"
									>
										{__('Clear', 'topbuddy')}
									</Button>
								)}
							</div>
						</div>

						<div className="topbuddy-schedule-control__field">
							<label className="topbuddy-settings-field__label">
								{__('End Date & Time', 'topbuddy')}
							</label>
							<div className="topbuddy-schedule-control__input-group">
								<Dropdown
									className="topbuddy-datetime-dropdown"
									contentClassName="topbuddy-datetime-dropdown__content"
									popoverProps={{ placement: 'bottom-start' }}
									renderToggle={({ isOpen, onToggle }) => (
										<Button
											onClick={onToggle}
											aria-expanded={isOpen}
											variant="secondary"
										>
											{localEndDate ? localEndDate.toLocaleString() : __('Select end date & time', 'topbuddy')}
										</Button>
									)}
									renderContent={() => (
										<DateTimePicker
											currentDate={localEndDate}
											onChange={handleEndDateChange}
											is12Hour={false}
										/>
									)}
								/>
								{endDate && (
									<Button
										variant="tertiary"
										isDestructive
										onClick={handleClearEndDate}
										className="topbuddy-schedule-control__clear"
									>
										{__('Clear', 'topbuddy')}
									</Button>
								)}
							</div>
						</div>

						{startDate && endDate && new Date(startDate) > new Date(endDate) && (
							<div className="topbuddy-schedule-control__error">
								{__('End date must be after start date', 'topbuddy')}
							</div>
						)}
					</>
				)}

				{scheduleType === 'days_of_week' && (
					<div className="topbuddy-schedule-control__field">
						<label className="topbuddy-settings-field__label">
							{__('Days of the Week', 'topbuddy')}
						</label>
						<ButtonGroup>
							{days.map((day) => (
								<Button
									key={day.key}
									variant={selectedDays.includes(day.key) ? 'primary' : 'secondary'}
									onClick={() => toggleDay(day.key)}
								>
									{day.label}
								</Button>
							))}
						</ButtonGroup>
					</div>
				)}
			</div>
		</div>
	);
};

export default ScheduleControl;
